import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { parseServerDate } from '../utils/dates.js';

// ── Markdown renderer (basic) ─────────────────────────────────────────────────
// Supports: ## headings, **bold**, *italic*, - lists, numbered lists, blank lines

function renderMarkdown(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listType = null;

  function flushList() {
    if (!listItems.length) return;
    const Tag = listType === 'ul' ? 'ul' : 'ol';
    elements.push(
      <Tag style={`margin:8px 0 8px ${listType === 'ul' ? '20px' : '24px'};padding:0;`}>
        {listItems.map((item, i) => (
          <li key={i} style="font-size:14px;line-height:1.7;color:var(--color-text);margin-bottom:2px"
            dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
        ))}
      </Tag>
    );
    listItems = [];
    listType = null;
  }

  function inlineFormat(str) {
    return str
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code style="background:var(--color-bg-secondary);padding:1px 5px;border-radius:4px;font-size:12px;font-family:monospace">$1</code>');
  }

  lines.forEach((line, i) => {
    // H2
    if (line.startsWith('## ')) {
      flushList();
      elements.push(<h3 key={i} style="font-size:16px;font-weight:700;margin:20px 0 8px;color:var(--color-text);border-bottom:1px solid var(--color-border);padding-bottom:6px">{line.slice(3)}</h3>);
      return;
    }
    // H3
    if (line.startsWith('### ')) {
      flushList();
      elements.push(<h4 key={i} style="font-size:14px;font-weight:600;margin:14px 0 6px;color:var(--color-text)">{line.slice(4)}</h4>);
      return;
    }
    // Unordered list
    if (line.match(/^- /)) {
      if (listType !== 'ul') { flushList(); listType = 'ul'; }
      listItems.push(line.slice(2));
      return;
    }
    // Ordered list
    if (line.match(/^\d+\. /)) {
      if (listType !== 'ol') { flushList(); listType = 'ol'; }
      listItems.push(line.replace(/^\d+\. /, ''));
      return;
    }
    // Blank line
    if (!line.trim()) {
      flushList();
      elements.push(<div key={i} style="height:8px" />);
      return;
    }
    // Normal paragraph
    flushList();
    elements.push(
      <p key={i} style="font-size:14px;line-height:1.75;margin:0 0 6px;color:var(--color-text)"
        dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />
    );
  });
  flushList();
  return elements;
}

// ── Category config ───────────────────────────────────────────────────────────

const CATEGORY_META = {
  algemeen:       { label: 'Algemeen',     icon: '🏠' },
  processen:      { label: 'Processen',    icon: '⚙️' },
  taken:          { label: 'Taken',        icon: '✅' },
  documenten:     { label: 'Documenten',   icon: '📄' },
  leden:          { label: 'Leden',        icon: '👥' },
  beheer:         { label: 'Beheer',       icon: '🔧' },
  sjablonen:      { label: 'Sjablonen',    icon: '📋' },
  rapportages:    { label: 'Rapportages',  icon: '📊' },
  sportlink:      { label: 'SportLink',    icon: '🔗' },
  ai:             { label: 'AI Assistent', icon: '🤖' },
  coins:          { label: 'Club Coins',   icon: '🪙' },
  marktplaats:    { label: 'Marktplaats',  icon: '🛒' },
  navigatie:      { label: 'Navigatie',    icon: '🧭' },
  communicatie:   { label: 'Communicatie', icon: '💬' },
  inzichten:      { label: 'Inzichten',    icon: '📈' },
  motivatie:      { label: 'Motivatie',    icon: '🏆' },
  integraties:    { label: 'Koppelingen',  icon: '🔗' },
};

const TYPE_COLORS = {
  handleiding:    { bg: '#dbeafe', color: '#1d4ed8', label: 'Handleiding' },
  werkinstructie: { bg: '#dcfce7', color: '#16a34a', label: 'Werkinstructie' },
};

function getCategoryMeta(cat) {
  return CATEGORY_META[cat] || { label: cat, icon: '📌' };
}

// ── Main HelpView ─────────────────────────────────────────────────────────────

export default function HelpView() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingArticle, setLoadingArticle] = useState(false);

  useEffect(() => {
    Promise.all([
      api('/api/help/articles'),
      api('/api/help/categories'),
    ])
      .then(([arts, cats]) => {
        setArticles(Array.isArray(arts) ? arts : []);
        setCategories(Array.isArray(cats) ? cats : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const openArticle = async (id) => {
    setLoadingArticle(true);
    try {
      const art = await api(`/api/help/articles/${id}`);
      setSelected(art);
    } finally {
      setLoadingArticle(false);
    }
  };

  const filtered = articles.filter(a => {
    if (activeCategory !== 'all' && a.category !== activeCategory) return false;
    if (activeType !== 'all' && a.article_type !== activeType) return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group filtered articles by category
  const grouped = filtered.reduce((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  return (
    <div>
      <div class="page-header mb-6">
        <div>
          <h1 class="page-title" style="margin:0">📚 Handleiding & werkinstructies</h1>
          <p style="font-size:14px;color:var(--color-text-muted);margin:4px 0 0">Alles wat je nodig hebt om met de app aan de slag te gaan</p>
        </div>
      </div>

      <div style="display:flex;gap:20px;align-items:flex-start">

        {/* Left sidebar */}
        <div style="width:220px;flex-shrink:0;position:sticky;top:80px">
          {/* Search */}
          <input
            class="form-input"
            type="text"
            placeholder="🔍 Zoeken..."
            value={search}
            onInput={e => { setSearch(e.target.value); setSelected(null); }}
            style="margin-bottom:12px;font-size:13px"
          />

          {/* Type filter */}
          <div style="margin-bottom:12px">
            <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:6px">Type</div>
            {[
              { val: 'all', label: 'Alles' },
              { val: 'handleiding', label: '📖 Handleiding' },
              { val: 'werkinstructie', label: '📋 Werkinstructie' },
            ].map(opt => (
              <button
                key={opt.val}
                onClick={() => { setActiveType(opt.val); setSelected(null); }}
                style={`display:block;width:100%;text-align:left;padding:6px 10px;border-radius:7px;border:none;cursor:pointer;font-size:13px;margin-bottom:2px;background:${activeType === opt.val ? 'var(--color-primary)' : 'transparent'};color:${activeType === opt.val ? '#fff' : 'var(--color-text)'};font-weight:${activeType === opt.val ? '600' : '400'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Category filter */}
          <div>
            <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:6px">Categorie</div>
            <button
              onClick={() => { setActiveCategory('all'); setSelected(null); }}
              style={`display:block;width:100%;text-align:left;padding:6px 10px;border-radius:7px;border:none;cursor:pointer;font-size:13px;margin-bottom:2px;background:${activeCategory === 'all' ? 'var(--color-primary)' : 'transparent'};color:${activeCategory === 'all' ? '#fff' : 'var(--color-text)'}`}
            >
              🗂 Alle categorieën
            </button>
            {categories.map(cat => {
              const meta = getCategoryMeta(cat);
              return (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setSelected(null); }}
                  style={`display:block;width:100%;text-align:left;padding:6px 10px;border-radius:7px;border:none;cursor:pointer;font-size:13px;margin-bottom:2px;background:${activeCategory === cat ? 'var(--color-primary)' : 'transparent'};color:${activeCategory === cat ? '#fff' : 'var(--color-text)'}`}
                >
                  {meta.icon} {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div style="flex:1;min-width:0">
          {selected ? (
            /* Article reader */
            <div>
              <button
                onClick={() => setSelected(null)}
                style="display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;color:var(--color-primary);font-size:13px;font-weight:500;padding:0;margin-bottom:16px"
              >
                ← Terug naar overzicht
              </button>
              <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:14px;padding:28px">
                {/* Article header */}
                <div style="margin-bottom:20px;padding-bottom:16px;border-bottom:1px solid var(--color-border)">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;flex-wrap:wrap">
                    {(() => {
                      const typeMeta = TYPE_COLORS[selected.article_type] || TYPE_COLORS.handleiding;
                      const catMeta = getCategoryMeta(selected.category);
                      return (
                        <>
                          <span style={`font-size:11px;font-weight:600;padding:2px 9px;border-radius:10px;background:${typeMeta.bg};color:${typeMeta.color}`}>{typeMeta.label}</span>
                          <span style="font-size:11px;background:var(--color-bg-secondary);border:1px solid var(--color-border);padding:2px 9px;border-radius:10px;color:var(--color-text-muted)">{catMeta.icon} {catMeta.label}</span>
                        </>
                      );
                    })()}
                  </div>
                  <h2 style="font-size:22px;font-weight:700;margin:0;line-height:1.3">{selected.title}</h2>
                  {selected.updated_at && (
                    <div style="font-size:12px;color:var(--color-text-muted);margin-top:8px">
                      Bijgewerkt op {parseServerDate(selected.updated_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  )}
                </div>
                {/* Article body */}
                <div>{renderMarkdown(selected.content)}</div>
              </div>
            </div>
          ) : loadingArticle ? (
            <div style="padding:40px;text-align:center;color:var(--color-text-muted)">Laden...</div>
          ) : loading ? (
            <div style="display:flex;flex-direction:column;gap:10px">
              {[0,1,2,3].map(i => <div key={i} class="skeleton" style="height:70px;border-radius:10px" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div style="text-align:center;padding:60px;color:var(--color-text-muted);border:1px dashed var(--color-border);border-radius:14px">
              <div style="font-size:36px;margin-bottom:12px">🔍</div>
              <p style="font-size:15px;font-weight:500;margin:0">Geen artikelen gevonden</p>
              <p style="font-size:13px;margin:6px 0 0">Probeer een andere zoekterm of categorie</p>
            </div>
          ) : (
            /* Article list grouped by category */
            <div style="display:flex;flex-direction:column;gap:20px">
              {Object.entries(grouped).map(([cat, arts]) => {
                const meta = getCategoryMeta(cat);
                return (
                  <div key={cat}>
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
                      <span style="font-size:18px">{meta.icon}</span>
                      <span style="font-size:15px;font-weight:700">{meta.label}</span>
                      <span style="font-size:12px;color:var(--color-text-muted)">({arts.length})</span>
                    </div>
                    <div style="display:flex;flex-direction:column;gap:6px">
                      {arts.map(art => {
                        const typeMeta = TYPE_COLORS[art.article_type] || TYPE_COLORS.handleiding;
                        return (
                          <div
                            key={art.id}
                            onClick={() => openArticle(art.id)}
                            style="display:flex;align-items:center;gap:12px;padding:14px 16px;background:var(--color-surface);border:1px solid var(--color-border);border-radius:10px;cursor:pointer;transition:border-color .15s,box-shadow .15s"
                            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-primary)'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border)'}
                          >
                            <div style="flex:1;min-width:0">
                              <div style="font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{art.title}</div>
                            </div>
                            <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:8px;white-space:nowrap;background:${typeMeta.bg};color:${typeMeta.color};flex-shrink:0`}>
                              {typeMeta.label}
                            </span>
                            <span style="color:var(--color-text-muted);font-size:16px;flex-shrink:0">›</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
